import mutations from './mutations.js';
import actions from './actions.js';
import getters from './getters.js';

export default {
  namespaced: true,
  state() {
    return {
      works: [
        {
          id: 'project10',
          title: 'Stitching Fairy 2.0',
          featured: true,
          skills: [
            'Vue 3',
            'TypeScript',
            'Vite SSG',
            'PHP',
            'MySQL',
            'REST API',
            'GitHub Actions',
            'SEO'
          ],
          shortDescription:
            'Full rebuild of a Dublin tailoring business website, with a custom admin panel for collections, photos and prices.',
          description: [
            'A complete rebuild of the Stitching Fairy website I first made in 2021. The front end is Vue 3 with TypeScript, pre-rendered with vite-ssg so every page loads fast and is fully indexable by search engines. Old URLs redirect automatically to keep existing search rankings.',
            'Behind it is a PHP 8.3 JSON API with MySQL and an admin panel where the owner manages everything herself: create, publish and reorder collections, drag-and-drop photo uploads (resized in the browser, converted to WebP in three sizes on the server, location data removed), cover images, descriptions and the alterations price list.',
            'Security was built in from the start: bcrypt passwords, HttpOnly SameSite session cookies, CSRF tokens on every change, login rate limiting and upload type and size checks.',
            'Deployments run through GitHub Actions to standard shared hosting. Every push goes to a test site first, and production is released manually. I also prepared two complete design directions for the owner to choose from.'
          ],
          image: 'stitchingfairy2/stitching-fairy-2.jpg',
          websiteLink: 'https://www.stitchingfairy.com/',
          githubLink: ''
        },
        {
          id: 'project9',
          title: 'Gabriel Professional',
          featured: true,
          skills: ['Vue 3', 'Vite', 'JavaScript', 'CSS', 'Vercel', 'SEO'],
          shortDescription:
            'Website for a hair spa in Smithfield, Dublin, with online booking, a gallery and the salon team.',
          description: [
            'A website for Gabriel Professional Hair Spa, a salon in Smithfield, Dublin, open since 2007. It is built with Vue 3 and Vite and hosted on Vercel.',
            'The site presents the services with two-tier pricing, the team, a gallery of client results with a swipe-enabled lightbox, and contact details with an embedded map. The booking buttons link straight to the salon\'s Phorest online booking system.',
            'Performance and SEO were a big part of the work: every image is optimised and served as WebP, mobile devices get their own lighter hero image, and the meta tags and page structure are set up for local search.'
          ],
          image: 'gabriel/gabriel-hair-spa.jpg',
          websiteLink: 'https://www.hairspa.ie/',
          githubLink: ''
        },
        {
          id: 'project8',
          title: 'Photographer',
          featured: true,
          skills: [
            'Vue',
            'Vuex',
            'Firebase',
            'Firestore',
            'Authentication',
            'JavaScript',
            'HTML',
            'Sass'
          ],
          shortDescription:
            'Portfolio website for a photographer, where you can create and delete collections and add or remove images in each collection.',
          description: [
            'The main feature of the website is full control over the displayed content.',
            'After logging in as admin, you can create and delete collections. Inside each collection you can add and remove single images and switch the view of the images between a slider and a grid.',
            'Selected files are shown in an image preview before they are uploaded.'
          ],
          image: 'photographer/photographer-mockup.jpg',
          websiteLink: 'https://photographer-9ee9c.web.app/',
          githubLink: 'https://github.com/Archezi/vue-photographer'
        },
        {
          id: 'project7',
          title: 'Invoice App',
          featured: true,
          skills: ['Vue', 'Firebase', 'HTML', 'Sass', 'JavaScript'],
          shortDescription:
            'Create, edit and delete invoices, all stored in a Firebase database.',
          description: [
            'A project inspired by www.frontendmentor.io. I picked this project to apply my knowledge of building a Vue.js application with Firebase integration.',
            'You can create, edit and delete invoices in the database, filter invoices by status and change the status of each invoice.'
          ],
          image: 'invoiceApp/invoice-app.jpg',
          websiteLink: 'https://invoice-app-f8170.web.app/',
          githubLink: 'https://github.com/Archezi/invoice-app'
        },
        {
          id: 'project6',
          title: 'Pomodoro',
          skills: ['Vue', 'HTML', 'Sass', 'JavaScript'],
          shortDescription: 'Customisable Pomodoro application.',
          description: [
            'You can customise your Pomodoro timer to suit the way you work. The application automatically switches to a break when your Pomodoro time runs out, and after 4 Pomodoros it switches to a long break.'
          ],
          image: 'pomodoro/pomodoro.jpg',
          websiteLink: 'https://pomodoro-36e2c.web.app/',
          githubLink: 'https://github.com/Archezi/pomodoro-app'
        },
        {
          id: 'project5',
          title: 'Budget',
          skills: ['HTML', 'Sass', 'JavaScript'],
          shortDescription: 'Budget application in vanilla JavaScript.',
          description: [
            'A project to showcase my understanding of the core concepts of the JavaScript language and how to apply them.'
          ],
          image: 'javascript/javascript-MacBook-Pro-mockup-front-page.jpg',
          websiteLink: 'https://archezi.github.io/BudgetApplication/',
          githubLink: 'https://github.com/Archezi/BudgetApplication'
        },
        {
          id: 'project4',
          title: 'Stitching Fairy 1.0',
          shortDescription:
            'The original website for a local tailoring company, since replaced by Stitching Fairy 2.0.',
          skills: ['HTML', 'Sass', 'JavaScript', 'Photoshop', 'Grid'],
          description: [
            'Stitching Fairy was my project for a small tailoring workshop that wanted to expand its range of services and update its previous website. For this project I created a logo, a style guide for the company and a website. The website was built with CSS Grid layout and Sass for styling.',
            'In 2026 I rebuilt the website from scratch as Stitching Fairy 2.0, so this version is no longer live.'
          ],
          image:
            'stitchingfairy/stitching-fairy-MacBook-Pro-mockup-front-page.jpg',
          websiteLink: '',
          githubLink: 'https://github.com/Archezi/stitchingfairyalternations'
        },
        {
          id: 'project3',
          title: 'NewProvidence',
          shortDescription:
            'A one-page mockup converted into a fully interactive website based on the Bootstrap framework.',
          skills: ['HTML', 'Sass', 'Bootstrap', 'JavaScript'],
          description: [
            'NewProvidence is a website designed by "GetCraftWork". As practice, I converted this design into a live, interactive website to demonstrate my knowledge of responsive development for modern websites. To complete this project I used Bootstrap 3 components and its grid system, and the wow.js library for a few visual effects.',
            'The project was created to demonstrate my HTML and CSS skills, but I also used some simple DOM manipulation in JavaScript to add interactions to the website.'
          ],
          image:
            'newprovidence/newprovidence-MacBook-Pro-mockup-front-page.jpg',
          websiteLink: 'https://archezi.github.io/newprovidence_1.0/',
          githubLink: 'https://github.com/Archezi/newprovidence_1.0'
        },
        {
          id: 'project2',
          title: 'Portfolio 2.0',
          shortDescription:
            'My second portfolio website, made with Vue.js.',
          skills: ['HTML', 'Sass', 'JavaScript', 'Vue', 'Vuex', 'Photoshop'],
          description: [
            'This project was all about applying and presenting my skills in the design and development of a website using Vue.js.'
          ],
          image: 'portfolioVue/portfolioVue-Mockup-MacBook.jpg',
          websiteLink: 'https://portfolio-vue-386d3.web.app/',
          githubLink: 'https://github.com/Archezi/portfolio-vue'
        },
        {
          id: 'project1',
          title: 'Portfolio 1.0',
          shortDescription:
            'My first portfolio website, gathering some of my early projects.',
          skills: ['HTML', 'Sass', 'JavaScript', 'Photoshop'],
          description: [
            'This project was all about applying and presenting my skills in the design and development of a website using vanilla JavaScript and Sass.'
          ],
          image: 'portfolio/portfolio-Mockup-MacBook.jpg',
          websiteLink: 'https://archezi.github.io/PortfolioWebsite/index.html',
          githubLink: 'https://github.com/Archezi/PortfolioWebsite'
        }
      ]
    };
  },
  mutations,
  actions,
  getters
};
